import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { getLibraryPlaylists } from "../api";

export default function LibrarySelectionModal({ handleClose, handleSelect }) {
  const navigate = useNavigate();
  const [state, setState] = useState({
    playlists: [],
    isLoading: true,
    error: "",
    expandedById: {},
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await getLibraryPlaylists();
        setState((s) => ({
          ...s,
          playlists: data?.playlists || [],
          isLoading: false,
        }));
      } catch (error) {
        console.error("Failed to load library playlists", error);
        setState((s) => ({
          ...s,
          isLoading: false,
          error: "Failed to load library playlists.",
        }));
      }
    })();
  }, []);

  const handleToggleExpand = (event, playlistId) => {
    event.stopPropagation();
    setState((s) => ({
      ...s,
      expandedById: {
        ...s.expandedById,
        [playlistId]: !s.expandedById[playlistId],
      },
    }));
  };

  const handleNavigateToLibrary = () => {
    handleClose();
    navigate("/library");
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      PaperProps={{
        style: {
          backgroundColor: "#d8c8f4",
          color: "#4b445b",
          width: "760px",
          maxWidth: "90vw",
          height: "620px",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle>Select Playlist From Library</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", minHeight: "500px" }}
      >
        {state.isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              minHeight: "240px",
            }}
          >
            <CircularProgress sx={{ color: "#4b445b" }} />
          </Box>
        ) : null}
        {!state.isLoading && state.error ? (
          <Typography color="error">{state.error}</Typography>
        ) : null}
        {!state.isLoading && !state.error && state.playlists.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
              textAlign: "center",
            }}
          >
            <Typography>There are no playlists added in your library yet</Typography>
            <Button
              onClick={handleNavigateToLibrary}
              startIcon={<ArrowForwardIcon fontSize="small" />}
              sx={{
                p: 0,
                minWidth: 0,
                textTransform: "none",
                fontWeight: 600,
                color: "#4b445b",
                backgroundColor: "transparent",
                border: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Create Playlist in Library
            </Button>
          </Box>
        ) : null}
        {!state.isLoading && !state.error && state.playlists.length > 0 ? (
          <List sx={{ p: 0, overflowY: "auto" }}>
            {state.playlists.map((playlist) => (
              <Box
                key={playlist.id}
                sx={{
                  mb: 1,
                  borderRadius: "8px",
                  border: "1px solid rgba(75, 68, 91, 0.2)",
                  backgroundColor: "rgba(255,255,255,0.35)",
                  overflow: "hidden",
                }}
              >
                <ListItemButton
                  onClick={() => handleSelect(playlist)}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.55)",
                    },
                  }}
                >
                  <ListItemText
                    primary={playlist.name}
                    secondary={`${playlist.playlist?.length || 0} songs`}
                  />
                  <IconButton
                    size="small"
                    onClick={(event) => handleToggleExpand(event, playlist.id)}
                  >
                    {state.expandedById[playlist.id] ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </ListItemButton>
                <Collapse in={!!state.expandedById[playlist.id]} timeout={220}>
                  <Divider />
                  <Box sx={{ px: 2, py: 1.5, backgroundColor: "rgba(255,255,255,0.2)" }}>
                    {(playlist.playlist || []).length === 0 ? (
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        No songs in this playlist.
                      </Typography>
                    ) : (
                      (playlist.playlist || []).map((song, index) => (
                        <Typography key={`${playlist.id}-${index}`} variant="body2">
                          {song.item}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </List>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
